// src/event/EventProvider.jsx
// Resolves the active event (brand) from the hostname and provides its config
// to the app. Never blocks render: config starts from localStorage cache or
// baked defaults, then live-updates from events/{eventId} once a user is
// signed in (current Firestore rules deny unauthenticated reads; a public
// read rule for events config ships with the Stage 7/8 rules work).
//
// configStatus (RETAINER_ABN_SUPPLIER_PLAN.md 5.8): 'cached' until the signed-in user's live
// events/{eventId} read has been confirmed by the server, then 'server'; 'error' if that
// subscription fails. Anything that must not act on a cached or default config (the retainer
// gate, the legacy /pays retainer popup) waits for 'server'. retryConfig() re-subscribes.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { resolveBrand } from './resolveEventId';
import { EVENT_DEFAULTS, NEUTRAL_EVENT_DEFAULT } from './eventDefaults';
import { EventContext } from './eventContext';

const CACHE_PREFIX = 'eventConfig:';
const CACHE_VERSION = 'brand-assets-v3';

function defaultsFor(eventId, brandStatus) {
  if (brandStatus === 'unknown') return NEUTRAL_EVENT_DEFAULT;
  return EVENT_DEFAULTS[eventId] || NEUTRAL_EVENT_DEFAULT;
}

// Merge the Firestore doc over baked defaults, section by section, so a doc
// that omits a key (e.g. a new feature flag) still gets the baked value.
function mergeConfig(eventId, docData, brandStatus) {
  const base = defaultsFor(eventId, brandStatus);
  if (!docData) return base;
  const merged = { ...base, ...docData };
  for (const section of ['brand', 'night', 'features', 'rules', 'entryPerks', 'retainerForm', 'managementFreeList', 'copy']) {
    merged[section] = { ...(base[section] || {}), ...(docData[section] || {}) };
  }
  merged.__frontDoorRulesFromFirestore = !!docData?.rules?.frontDoor;
  return merged;
}

function cleanStr(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function mergePublicBrandConfig(eventId, currentConfig, publicData, brandStatus) {
  const base = currentConfig || defaultsFor(eventId, brandStatus);
  const data = publicData && typeof publicData === 'object' ? publicData : {};
  const publicBrand = data.brand && typeof data.brand === 'object' ? data.brand : {};
  const publicBaseUrls = data.baseUrls && typeof data.baseUrls === 'object' ? data.baseUrls : {};
  const brand = { ...(base.brand || {}), ...publicBrand };
  const logo = cleanStr(publicBrand.logo) || cleanStr(data.logo);
  const logoSymbol = cleanStr(publicBrand.logoSymbol) || cleanStr(data.logoSymbol);
  const icon = cleanStr(publicBrand.icon) || cleanStr(data.icon);
  if (logo) brand.logo = logo;
  if (logoSymbol) brand.logoSymbol = logoSymbol;
  if (icon) brand.icon = icon;
  return { ...base, baseUrls: { ...(base.baseUrls || {}), ...publicBaseUrls }, brand };
}

function readCache(eventId) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + eventId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.__cacheVersion !== CACHE_VERSION) return null;
    return parsed.config || null;
  } catch {
    return null;
  }
}

function writeCache(eventId, config) {
  try {
    localStorage.setItem(CACHE_PREFIX + eventId, JSON.stringify({ __cacheVersion: CACHE_VERSION, config }));
  } catch {
    /* storage unavailable — live config still works for this session */
  }
}

export function EventProvider({ children }) {
  // resolveBrand() returns { eventId, status }. eventId is always a valid string
  // (DEFAULT for unknown hosts) so the app never crashes; brandStatus === 'unknown'
  // flags an unconfigured production host (a future neutral 'brand unavailable'
  // shell + write-blocking consumes it). Render is never blocked here.
  const [brandRes] = useState(() => resolveBrand(window.location.hostname));
  const eventId = brandRes.eventId;
  const brandStatus = brandRes.status;
  const [config, setConfig] = useState(() =>
    brandStatus === 'unknown' ? defaultsFor(eventId, brandStatus) : readCache(eventId) || defaultsFor(eventId, brandStatus)
  );
  // Live-config confirmation for the CURRENT signed-in user only (uid-keyed, so a new user
  // never sees the previous user's 'server').
  const [configState, setConfigState] = useState({ status: 'cached', uid: null });
  const [authUid, setAuthUid] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (brandStatus === 'unknown') return undefined;
    // Active brandPublic is the safe pre-login branding source. Under older rules this may be
    // permission-denied; in that case baked defaults still keep the app bootable.
    const unsubPublic = onSnapshot(
      doc(db, 'brandPublic', eventId),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        if (!['active', 'paused'].includes(data.status)) return;
        setConfig((cur) => {
          const merged = mergePublicBrandConfig(eventId, cur, data, brandStatus);
          writeCache(eventId, merged);
          return merged;
        });
      },
      (err) => {
        if (err?.code !== 'permission-denied') {
          console.warn('[event] public brand subscribe error:', err?.code || err);
        }
      }
    );
    return () => unsubPublic();
  }, [eventId, brandStatus]);

  useEffect(() => {
    let unsubSnap = null;
    let generation = 0;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      generation += 1;
      const mine = generation;
      const uid = user ? user.uid : null;
      setAuthUid(uid);
      setConfigState({ status: 'cached', uid });
      if (!user || brandStatus === 'unknown') return; // pre-login/unknown host: cached/default config is enough

      let lastJson = null;
      unsubSnap = onSnapshot(
        doc(db, 'events', eventId),
        // Metadata changes too, so a cache-to-server confirmation of unchanged data still
        // flips configStatus to 'server'.
        { includeMetadataChanges: true },
        (snap) => {
          if (mine !== generation) return; // a callback for a previous user/subscription
          const fromServer = !snap.metadata.fromCache;
          if (!snap.exists()) {
            // doc not seeded yet: keep defaults. Only a server-confirmed miss counts as known.
            if (fromServer) {
              setConfig(defaultsFor(eventId, brandStatus));
              setConfigState({ status: 'server', uid });
            }
            return;
          }
          const data = snap.data();
          const json = JSON.stringify(data);
          if (json !== lastJson) {
            lastJson = json;
            const merged = mergeConfig(eventId, data, brandStatus);
            setConfig(merged);
            writeCache(eventId, merged);
          }
          if (fromServer) setConfigState({ status: 'server', uid });
        },
        (err) => {
          if (mine !== generation) return;
          console.warn('[event] config subscribe error:', err?.code || err);
          setConfigState({ status: 'error', uid });
        }
      );
    });

    return () => {
      generation += 1;
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, [eventId, brandStatus, retryNonce]);

  // A terminated listener is never reused: retry opens a fresh subscription.
  const retryConfig = useCallback(() => setRetryNonce((n) => n + 1), []);
  const configStatus = configState.uid === authUid ? configState.status : 'cached';

  const value = useMemo(
    () => ({
      eventId,
      brandStatus,
      brandUnavailable: brandStatus === 'unknown',
      config,
      brand: config.brand || {},
      baseUrls: config.baseUrls || {},
      runtime: config.runtime || {},
      night: config.night || {},
      features: config.features || {},
      rules: config.rules || {},
      entryPerks: config.entryPerks || {},
      copy: config.copy || {},
      retainerForm: config.retainerForm || {},
      configStatus,
      retryConfig
    }),
    [eventId, brandStatus, config, configStatus, retryConfig]
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}
